import React from "react";
import { Badge, Card, CardBody, CardHeader, Table } from "reactstrap";

function accClass(pct) {
  if (pct === null || pct === undefined) return "";
  if (pct >= 90) return "sc-stat--acc-high";
  if (pct >= 70) return "sc-stat--acc-mid";
  return "sc-stat--acc-low";
}

/**
 * Renders the final report for a completed or cancelled stock check session.
 *
 * Props
 *   report — object from GET /sessions/:id/report
 */
function StockCheckReportCard({ report }) {
  if (!report) return null;

  const s       = report.summary      || {};
  const missing = report.missingItems  || [];
  const extra   = report.extraItems    || [];
  const unknown = report.unknownBarcodes || [];

  return (
    <div>
      {/* Accuracy headline */}
      <Card className="sc-report-section">
        <CardHeader>
          <h5 className="sc-report-section__title">Accuracy</h5>
        </CardHeader>
        <CardBody>
          <div className="sc-metrics">
            <div className={`sc-metric ${accClass(s.inventoryAccuracyPct)}`}>
              <div className="sc-metric__value">{s.inventoryAccuracyPct ?? "—"}%</div>
              <div className="sc-metric__label">Inventory Accuracy</div>
            </div>
            <div className={`sc-metric ${accClass(s.availabilityAccuracyPct)}`}>
              <div className="sc-metric__value">{s.availabilityAccuracyPct ?? "—"}%</div>
              <div className="sc-metric__label">Availability</div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Unit totals */}
      <Card className="sc-report-section">
        <CardHeader>
          <h5 className="sc-report-section__title">Unit Summary</h5>
        </CardHeader>
        <CardBody>
          <div className="sc-metrics">
            <div className="sc-metric sc-stat--remaining">
              <div className="sc-metric__value">{s.totalExpectedUnits ?? 0}</div>
              <div className="sc-metric__label">Expected</div>
            </div>
            <div className="sc-metric sc-stat--remaining">
              <div className="sc-metric__value">{s.totalScannedUnits ?? 0}</div>
              <div className="sc-metric__label">Scanned</div>
            </div>
            <div className="sc-metric sc-stat--extra">
              <div className="sc-metric__value">{s.missingUnits ?? 0}</div>
              <div className="sc-metric__label">Missing</div>
            </div>
            <div className="sc-metric sc-stat--extra">
              <div className="sc-metric__value">{s.extraUnits ?? 0}</div>
              <div className="sc-metric__label">Extra</div>
            </div>
            <div className="sc-metric sc-stat--unknown">
              <div className="sc-metric__value">{s.unknownBarcodeCount ?? 0}</div>
              <div className="sc-metric__label">Unknown</div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Missing items */}
      {missing.length > 0 && (
        <Card className="sc-report-section">
          <CardHeader>
            <h5 className="sc-report-section__title" style={{ color: "#fd5d93" }}>
              Missing Items ({missing.length})
            </h5>
          </CardHeader>
          <CardBody>
            <div style={{ overflowX: "auto" }}>
              <Table responsive size="sm" style={{ marginBottom: 0, fontSize: "0.83rem" }}>
                <thead className="text-primary">
                  <tr>
                    <th>Product</th>
                    <th>Variant</th>
                    <th style={{ textAlign: "right" }}>Expected</th>
                    <th style={{ textAlign: "right" }}>Scanned</th>
                    <th style={{ textAlign: "right" }}>Missing</th>
                  </tr>
                </thead>
                <tbody>
                  {missing.map((item) => (
                    <tr key={item.id}>
                      <td>{item.productTitle || "—"}</td>
                      <td>{item.variantTitle || "—"}</td>
                      <td style={{ textAlign: "right" }}>{item.expectedQty}</td>
                      <td style={{ textAlign: "right" }}>{item.scannedQty}</td>
                      <td style={{ textAlign: "right" }}>
                        <Badge color="danger" pill>
                          {item.remainingQty ?? Math.max(0, item.expectedQty - item.scannedQty)}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Extra items */}
      {extra.length > 0 && (
        <Card className="sc-report-section">
          <CardHeader>
            <h5 className="sc-report-section__title" style={{ color: "#ff8d72" }}>
              Extra Items ({extra.length})
            </h5>
          </CardHeader>
          <CardBody>
            <div style={{ overflowX: "auto" }}>
              <Table responsive size="sm" style={{ marginBottom: 0, fontSize: "0.83rem" }}>
                <thead className="text-primary">
                  <tr>
                    <th>Product</th>
                    <th>Variant</th>
                    <th style={{ textAlign: "right" }}>Scanned</th>
                  </tr>
                </thead>
                <tbody>
                  {extra.map((item) => (
                    <tr key={item.id}>
                      <td>{item.productTitle || "—"}</td>
                      <td>{item.variantTitle || "—"}</td>
                      <td style={{ textAlign: "right" }}>
                        <Badge color="warning" pill>{item.scannedQty}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Unknown barcodes — shape: [{ barcode, scanCount }] */}
      {unknown.length > 0 && (
        <Card className="sc-report-section">
          <CardHeader>
            <h5 className="sc-report-section__title" style={{ color: "#ff8d72" }}>
              Unknown Barcodes ({unknown.length})
            </h5>
          </CardHeader>
          <CardBody>
            <div className="sc-unknown-tags">
              {unknown.map((u) => (
                <Badge key={u.barcode} color="warning" pill>
                  {u.barcode}
                  {u.scanCount > 1 && (
                    <span style={{ marginLeft: 4, opacity: 0.7 }}>×{u.scanCount}</span>
                  )}
                </Badge>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {missing.length === 0 && extra.length === 0 && unknown.length === 0 && (
        <div className="text-center py-3">
          <Badge color="success" style={{ fontSize: "0.9rem", padding: "8px 16px" }}>
            ✓ Perfect count — no discrepancies found!
          </Badge>
        </div>
      )}
    </div>
  );
}

export default StockCheckReportCard;
